/**
 * Light or dark, from the theme chosen in Settings (`ui.theme`).
 *
 * "System default" follows the operating system and changes live when it
 * does. The resolved mode is written to `<html data-theme>` — the switch for
 * every colour token in `tokens.css` — and builds the MUI theme, so CSS and
 * MUI components always agree. The choice lives in Redux (`ui.theme`,
 * persisted), and the attribute is set in a layout effect, before paint.
 */

import {
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { useAppSelector } from '../store';
import { makeTheme, type Mode } from './MuiTheme';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function systemMode(): Mode {
	return typeof window !== 'undefined' &&
		window.matchMedia?.(DARK_QUERY).matches
		? 'dark'
		: 'light';
}

export function AppThemeProvider({ children }: { children: ReactNode }) {
	const choice = useAppSelector((state) => state.ui.theme);
	const [system, setSystem] = useState<Mode>(systemMode);

	/* Follow the OS while "System default" is chosen. */
	useEffect(() => {
		const query = window.matchMedia?.(DARK_QUERY);
		if (!query) return;
		const update = () => setSystem(query.matches ? 'dark' : 'light');
		query.addEventListener('change', update);
		return () => query.removeEventListener('change', update);
	}, []);

	const mode: Mode = choice === 'system' ? system : choice;

	useLayoutEffect(() => {
		const root = document.documentElement;
		root.dataset.theme = mode;
		root.style.colorScheme = mode;
	}, [mode]);

	const theme = useMemo(() => makeTheme(mode), [mode]);
	return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
